<script setup lang="ts">
import {
  mdiLinkVariant,
  mdiHeart,
  mdiHeartOutline,
  mdiDotsVertical,
  mdiArrowLeft,
  mdiInformationOutline,
  mdiFullscreenExit,
} from '@mdi/js'
import type { Work } from '~/composables/api/useWorkApi'

// 没入プレイのオーバーレイヘッダー（#443 観点A で WorkPlay から抽出）。
// Public Work の全画面ルート（WorkPlay）と、アプリ内 PlayDialog の fullscreen モードで共有する。
// 本コンポーネントは表示のみを担い、いいね/共有の実ロジックは親（usePlayActions）が持つ。
// ヘッダーの自動格納だけは UI 挙動なので内部で管理する。

withDefaults(defineProps<{
  title?: string
  author?: string
  authorSlug?: string | null
  canShare?: boolean
  isLiked?: boolean
  displayLikes?: number
  /** #752: お気に入り★のトグル対象 Work。null の間は★を出さない。 */
  work?: Work | null
  /** モーダルに戻す「ウィンドウ表示に戻す」ボタンを出すか（デスクトップのトグル時のみ true）。 */
  minimizable?: boolean
  /** Alt+Enter の全画面ヒントを出すか。 */
  showFullscreenHint?: boolean
  /**
   * #844: 「ほかの作品」の行き先。**公開プレイ面（`WorkPlay`）だけが渡す**。
   * アプリ内の `PlayDialog`（ゆりかご / 魔法研究会）から公開ギャラリーへ飛ばすと
   * 作業中の文脈を落とすので、既定では出さない。
   */
  moreWorksTo?: string
}>(), {
  showFullscreenHint: true,
})

const emit = defineEmits(['back', 'minimize', 'like', 'shareX', 'copyLink', 'openInfo', 'moreWorks'])

// ヘッダーの自動格納と再表示（WorkPlay から移設）。
// 初回マウント時は SNS 等から流入したユーザーに UI を把握させるため長めの 10 秒。
// ユーザー操作後は通常運用の 6 秒に短縮。マウス移動でも再表示する。
const HEADER_HIDE_INITIAL_MS = 10000
const HEADER_HIDE_REVEAL_MS = 6000

const headerVisible = ref(true)
let hideTimerId: ReturnType<typeof setTimeout> | null = null
let mouseMoveDebounceId: ReturnType<typeof setTimeout> | null = null

const scheduleHide = (delay: number) => {
  if (hideTimerId !== null) clearTimeout(hideTimerId)
  hideTimerId = setTimeout(() => {
    headerVisible.value = false
    hideTimerId = null
  }, delay)
}

const revealHeader = () => {
  headerVisible.value = true
  scheduleHide(HEADER_HIDE_REVEAL_MS)
}

const onWindowMouseMove = () => {
  // 200ms デバウンスで Vue リアクティビティの負荷を抑える
  if (mouseMoveDebounceId !== null) return
  mouseMoveDebounceId = setTimeout(() => {
    mouseMoveDebounceId = null
  }, 200)
  revealHeader()
}

onMounted(() => {
  scheduleHide(HEADER_HIDE_INITIAL_MS)
  window.addEventListener('mousemove', onWindowMouseMove, { passive: true })
})

onUnmounted(() => {
  if (hideTimerId !== null) clearTimeout(hideTimerId)
  if (mouseMoveDebounceId !== null) clearTimeout(mouseMoveDebounceId)
  window.removeEventListener('mousemove', onWindowMouseMove)
})
</script>

<template>
  <header
    class="play-header"
    :data-hidden="headerVisible ? null : ''"
    @pointerdown="revealHeader"
  >
    <div class="play-header__row play-header__row--title">
      <h1 class="title">
        {{ title }}
      </h1>
    </div>
    <div class="play-header__row play-header__row--actions">
      <!-- デスクトップのワイド表示: ← の位置に「標準表示に戻す」（アイコンのみ＋ツールチップ） -->
      <v-tooltip
        v-if="minimizable"
        text="標準表示に戻す"
        location="bottom"
      >
        <template #activator="{ props: tipProps }">
          <v-btn
            v-bind="tipProps"
            class="minimize-btn"
            variant="text"
            density="comfortable"
            :icon="mdiFullscreenExit"
            color="white"
            aria-label="標準表示に戻す"
            @click="emit('minimize')"
          />
        </template>
      </v-tooltip>
      <!-- #844: 公開プレイ面は「← 戻る」と「ほかの作品」を **1 つに畳む**。
           2 つは行き先が違う（`←` は `history.back()`、こちらは常に公開ギャラリー）が、
           **直リンク流入では `←` がサイト外への離脱そのもの**で、サイト内から来た人に
           とっても着地はどちらも「作品の一覧」でほぼ同じ。並べると主従が取りにくいので、
           常にギャラリーへ送る 1 つの出口にする。
           `NuxtLink` にするのは本物の `<a>` にして中クリックや「新しいタブで開く」を
           効かせるため（計測は親が拾う）。 -->
      <NuxtLink
        v-else-if="moreWorksTo"
        class="more-works-link"
        :to="moreWorksTo"
        @click="emit('moreWorks')"
      >
        <v-icon
          class="more-works-link__icon"
          :icon="mdiArrowLeft"
          aria-hidden="true"
        />ほかの作品</NuxtLink>
      <!-- アプリ内の PlayDialog（ゆりかご / 魔法研究会）: ← で閉じる/戻る -->
      <v-btn
        v-else
        class="back-btn"
        variant="text"
        density="comfortable"
        :icon="mdiArrowLeft"
        aria-label="戻る"
        @click="emit('back')"
      />
      <span
        v-if="author"
        class="work-provider"
      >by <NuxtLink
        v-if="authorSlug"
        :to="`/c/${authorSlug}`"
        class="author-link"
      >{{ author }}</NuxtLink><template v-else>{{ author }}</template></span>
      <v-spacer />
      <span
        v-if="showFullscreenHint"
        class="fullscreen-hint"
      >
        画面をクリックした後、<kbd class="fullscreen-hint__kbd">Alt + Enter</kbd> で全画面モード
      </span>
      <v-spacer />
      <v-btn
        v-if="canShare"
        class="like-btn"
        variant="text"
        density="comfortable"
        :prepend-icon="isLiked ? mdiHeart : mdiHeartOutline"
        :color="isLiked ? 'red-lighten-3' : 'white'"
        :aria-label="isLiked ? 'いいねを取り消す' : 'いいね'"
        @click="emit('like')"
      >
        {{ displayLikes }}
      </v-btn>
      <!-- お気に入り★（#752）。いいね♥（公開の称賛）と役割を分け、右端に目立たせる。
           トグル/未ログイン CTA のロジックは WorkFavoriteButton に集約済み（#724/#751）。 -->
      <div
        v-if="work"
        class="fav-btn"
      >
        <WorkFavoriteButton :work="work" />
      </div>
      <v-menu v-if="canShare">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            variant="text"
            density="comfortable"
            :icon="mdiDotsVertical"
            color="white"
            aria-label="メニュー"
          />
        </template>
        <v-list>
          <v-list-item
            :prepend-icon="mdiInformationOutline"
            title="作品について"
            @click="emit('openInfo')"
          />
          <v-list-item
            :prepend-icon="X_LOGO_ICON"
            title="X でシェア"
            @click="emit('shareX')"
          />
          <v-list-item
            :prepend-icon="mdiLinkVariant"
            title="リンクをコピー"
            @click="emit('copyLink')"
          />
        </v-list>
      </v-menu>
    </div>
  </header>
  <!-- ヘッダー格納時の薄い C&M アイコンロゴ。ヘッダー表示中は隠す -->
  <img
    v-show="!headerVisible"
    class="play-header__brand-mark"
    src="@/assets/images/code_and_magic_black_iconlogo.svg"
    alt="Code & Magic"
    aria-hidden="true"
  >
  <button
    v-if="!headerVisible"
    class="play-header__reveal"
    aria-label="ヘッダーを再表示"
    @click="revealHeader"
  />
</template>

<style scoped>
.play-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: env(safe-area-inset-top);
  transition: transform 0.25s ease, opacity 0.25s ease;
  transform-origin: top;
}

.play-header[data-hidden] {
  transform: translateY(-100%);
  opacity: 0;
  pointer-events: none;
}

.play-header__reveal {
  /* fixed: ビューポート起点で iframe 上端に重ねる */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: calc(24px + env(safe-area-inset-top));
  z-index: 3;
  background: transparent;
  border: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.play-header__row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  height: 40px;
}

.play-header__row--title {
  height: 44px;
  justify-content: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.play-header__row--actions {
  font-family: "Noto Sans JP", sans-serif;
}

.work-provider {
  /* WorkCard と同じ水色 */
  color: #60e0f0;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50%;
}

/* #844: 公開プレイ面の唯一の出口（← と統合済み）。作者リンク（水色）とは
   別の白系にして「サイト内の移動先」であることを見た目でも分ける。 */
.more-works-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #fff;
  font-size: 0.85rem;
  white-space: nowrap;
  padding: 4px 10px 4px 6px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  text-decoration: none;
}

.more-works-link__icon {
  font-size: 20px;
}

.more-works-link:hover {
  background: rgba(255, 255, 255, 0.15);
}

.author-link {
  color: inherit;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
}

.author-link:hover {
  text-decoration-style: solid;
}

.play-header__brand-mark {
  /* ヘッダー格納時に左上に薄く出すブランドマーク */
  position: fixed;
  top: calc(8px + env(safe-area-inset-top));
  left: 8px;
  width: 32px;
  height: 32px;
  z-index: 1;
  opacity: 0.18;
  pointer-events: none;
  /* 黒い svg を白く反転 */
  filter: invert(1) drop-shadow(#000 0 0 4px);
}

.like-btn,
.minimize-btn {
  color: #fff;
}

/* お気に入り★（#752）。暗いヘッダー地に合わせて白＋適度なタップ余白。
   ★アイコンは em ベースなので font-size でサイズを揃える。 */
.fav-btn {
  display: inline-flex;
  align-items: center;
  color: #fff;
  font-size: 24px;
  padding: 4px;
}

.fullscreen-hint {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: "Noto Sans JP", sans-serif;
}

.fullscreen-hint__kbd {
  display: inline-block;
  padding: 0 4px;
  margin: 0 2px;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.95);
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  letter-spacing: 0.02em;
}

/* 物理キーボード非保持（モバイル等）には Alt+Enter ヒントを出さない */
@media (pointer: coarse) {
  .fullscreen-hint {
    display: none;
  }
}

/* 狭い画面では文字が混み合うので隠す（タッチデバイスでなくとも閉じる） */
@media (max-width: 720px) {
  .fullscreen-hint {
    display: none;
  }
}

.back-btn {
  flex: 0 0 auto;
  color: #fff;
}

.title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  font-family: "Noto Sans JP", sans-serif;
}
</style>
